/**
 * `annotationAnchorPart.ts` — which part a comment is attached to, read off
 * the geometry its anchor sits on (web-only).
 *
 * A web comment stores its anchor as a world-space point with no parent part
 * (`models/data/annotationData.ts`), so the part is not on the row. The anchor
 * is a point ON the part's surface — the Comment tool takes it from a click on
 * the part, `create_annotation` from a ray that hit it — so a short ray aimed
 * at it from the side it was placed from meets that surface right there. The
 * probe tries the side facing the camera first (most comments are read from
 * about where they were placed), then every other side, and keeps the first
 * surface it meets within {@link K_ON_SURFACE_M} of the anchor.
 *
 * One anchor is not on a surface by design: Spark attaches a comment to the
 * CENTRE of a part's bounds when no point of the part is in view
 * (`annotationPlacement.ts`). An anchor no surface claims is therefore matched
 * against the bounds centres of the candidate parts, the smallest part first.
 *
 * It answers nothing — rather than a neighbour — for an anchor that is neither:
 * a part moved, exploded or hidden since the comment was placed. The pick goes
 * through `PickingService`, so batched and merged parts resolve the same way a
 * click on them does.
 */

import { Box3, Ray, Vector3, type Object3D } from "three";
import { Ioc } from "@core/ioc/ioc";
import type { AnnotationVec3 } from "@models/data/annotationData";
import { ENGINE_SERVICE, type EngineService } from "@runtime/engine/engineService";
import { PICKING_SERVICE, type PickingService } from "@runtime/picking/pickingService";
import { unityWorldPositionToThree } from "@runtime/assets/participantTransformConversion";
import { stageToWorldPosition } from "@runtime/xr/stageSpace";

/** How far from the anchor each probe ray starts, in world metres. */
const K_PROBE_REACH_M = 0.01;
/** A surface this close to the anchor is the one it sits on. */
const K_ON_SURFACE_M = 0.002;
/** Relative size difference read as equal bounds, absorbing float error. */
const K_SIZE_TIE = 1e-6;

/** The 26 directions to the faces, edges and corners of a cube around the anchor. */
const K_PROBE_DIRECTIONS: ReadonlyArray<Vector3> = (() => {
  const directions: Vector3[] = [];
  for (let x = -1; x <= 1; x++) {
    for (let y = -1; y <= 1; y++) {
      for (let z = -1; z <= 1; z++) {
        if (x !== 0 || y !== 0 || z !== 0) directions.push(new Vector3(x, y, z).normalize());
      }
    }
  }
  return directions;
})();

interface PartCentre {
  readonly id: string;
  readonly centre: Vector3;
  readonly size: number;
}

/**
 * The snapshot id of the part each anchor sits on, or null for one that sits
 * on no part. Null for the whole call when there is no viewport to probe.
 * `ignoredIds` are snapshot ids that are not parts — the comment bubbles.
 * `candidateIds` are the parts an anchor at a bounds centre may belong to,
 * ancestors before descendants.
 */
export function findAnnotationAnchorParts(
  anchors: ReadonlyArray<AnnotationVec3>,
  ignoredIds: ReadonlySet<string>,
  candidateIds: ReadonlyArray<string>,
): Array<string | null> | null {
  const engine = Ioc.isRegistered<EngineService>(ENGINE_SERVICE)
    ? Ioc.resolve<EngineService>(ENGINE_SERVICE)
    : null;
  const stageRoot = engine?.rootOrNull ?? null;
  const camera = stageRoot ? engine!.app.camera : null;
  if (!stageRoot || !camera || !Ioc.isRegistered<PickingService>(PICKING_SERVICE)) return null;
  const picker = Ioc.resolve<PickingService>(PICKING_SERVICE);
  const cameraPosition = camera.getWorldPosition(new Vector3());

  const isPart = (node: Object3D): boolean => {
    const id = picker.resolveSnapshotId(node);
    return id !== null && !ignoredIds.has(id);
  };
  const ray = new Ray();
  const anchorWorld = new Vector3();
  const toCamera = new Vector3();

  // Measured only when some anchor misses every surface, since it walks every
  // candidate's geometry.
  let centres: PartCentre[] | null = null;
  const partCentredAt = (point: Vector3): string | null => {
    centres ??= candidateIds.flatMap((id) => {
      const node = picker.resolveSnapshotNode(id);
      const bounds = node ? new Box3().setFromObject(node) : null;
      return bounds && !bounds.isEmpty()
        ? [{ id, centre: bounds.getCenter(new Vector3()), size: bounds.getSize(new Vector3()).length() }]
        : [];
    });
    // A model's root and its only mesh share their bounds; the later, deeper
    // candidate wins a tie as the more specific part.
    let best: PartCentre | null = null;
    for (const part of centres) {
      if (part.centre.distanceTo(point) > K_ON_SURFACE_M) continue;
      if (!best || part.size <= best.size * (1 + K_SIZE_TIE)) best = part;
    }
    return best?.id ?? null;
  };

  return anchors.map((anchor) => {
    stageToWorldPosition(stageRoot, unityWorldPositionToThree(anchor), anchorWorld);
    toCamera.copy(cameraPosition).sub(anchorWorld).normalize();
    const directions = [...K_PROBE_DIRECTIONS].sort((a, b) => b.dot(toCamera) - a.dot(toCamera));
    for (const direction of directions) {
      ray.origin.copy(anchorWorld).addScaledVector(direction, K_PROBE_REACH_M);
      ray.direction.copy(direction).negate();
      const hit = picker.pickFromRay(ray, isPart);
      if (hit?.snapshotId && Math.abs(hit.distance - K_PROBE_REACH_M) <= K_ON_SURFACE_M) {
        return hit.snapshotId;
      }
    }
    return partCentredAt(anchorWorld);
  });
}
